import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Anchor, Ship } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const MarineOffshore = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/marine-offshore"]?.title} description={seoData["services/marine-offshore"]?.description} path="/services/marine-offshore" keywords={seoData["services/marine-offshore"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Marine & Offshore Services"
        description="Marine coating contractor serving California ports, shipyards, and offshore platforms. Ballast tank coating, hull protection, rudder repair, and splash zone corrosion protection for vessels and marine infrastructure."
        backgroundKey="marine-offshore"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Ballast tank corrosion and coating breakdown',
              'Rudder and stern tube erosion',
              'Splash zone corrosion on pilings and marine structures',
              'Sea chest fouling and corrosion',
              'Deck coating wear and non-skid deterioration',
              'Emergency damage requiring rapid drydock repair'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg">
                <Anchor className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Ballast Tank & Void Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Marine-grade epoxy coating systems for ballast tanks, void spaces, and internal compartments. Surface-tolerant systems for application in confined spaces with limited ventilation and surface preparation access.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Rudder & Stern Tube Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">In-situ rudder bearing repair, stern tube restoration, and sea chest rebuild using cold-applied marine composites. Avoid costly drydock extensions for mechanical repairs.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Splash Zone & Pile Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion protection for splash zone areas on pilings, fenders, and marine structures using surface-tolerant, immersion-rated coating and wrap systems.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Deck & Non-Skid Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Deck coating and non-skid systems for working decks, walkways, and helicopter landing areas. Anti-corrosion protection with slip-resistant finishes.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['TWIC credentialed for port and terminal access',
                  'Rapid drydock repair to minimize vessel downtime',
                  'Surface-tolerant systems for challenging marine conditions',
                  'IMO and USCG compliant coating systems',
                  'Confined space certified for tank and void entry',
                  'Emergency mobilization for vessel damage repair',
                  'California port and shipyard experience',
                  'OSHA Maritime certified crews'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Rudder and stern tube repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Ballast tank coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1831 (Super UW-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Underwater and splash zone repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Surface-tolerant marine coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Marine or Offshore Corrosion? We Fix It.</h2>
          <p className="text-blue-100 mb-8">Contact our marine coating specialists for vessel, port, or offshore repair. Emergency drydock services available.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarineOffshore;
